import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, DollarSign, Users, Building2 } from 'lucide-react';
import api from '../lib/api';

interface Employee {
  id: string;
  employmentStatus: string;
  baseSalary?: number | string;
  commissionRate?: number | string;
  person?: {
    name?: string;
  };
  department?: {
    id?: string;
    name?: string;
  };
}

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const normalized = value
      .replace(/\s/g, '')
      .replace(/[R$]/g, '')
      .replace(/\./g, '')
      .replace(/,/g, '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);

export default function PayrollPage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employees?limit=1000');
      setEmployees(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar folha:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const activeEmployees = useMemo(
    () => employees.filter((employee) => employee.employmentStatus === 'ACTIVE'),
    [employees],
  );

  const totals = useMemo(() => {
    const payrollBase = activeEmployees.reduce(
      (acc, employee) => acc + toNumber(employee.baseSalary),
      0,
    );
    const averageSalary = activeEmployees.length > 0 ? payrollBase / activeEmployees.length : 0;
    return {
      payrollBase,
      averageSalary,
      totalActive: activeEmployees.length,
      totalEmployees: employees.length,
    };
  }, [activeEmployees, employees.length]);

  const byDepartment = useMemo(() => {
    const map = new Map<
      string,
      { name: string; count: number; payroll: number; avgCommissionRate: number }
    >();

    for (const employee of activeEmployees) {
      const departmentName = employee.department?.name || 'Sem departamento';
      const current = map.get(departmentName) || {
        name: departmentName,
        count: 0,
        payroll: 0,
        avgCommissionRate: 0,
      };
      const commissionRate = toNumber(employee.commissionRate);

      current.count += 1;
      current.payroll += toNumber(employee.baseSalary);
      current.avgCommissionRate += commissionRate;
      map.set(departmentName, current);
    }

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        avgCommissionRate: item.count > 0 ? item.avgCommissionRate / item.count : 0,
      }))
      .sort((a, b) => b.payroll - a.payroll);
  }, [activeEmployees]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando folha...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Folha de Pagamento</h1>
        <p className="text-gray-600 mt-1">
          Dados oficiais calculados a partir de funcionários ativos da base
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Funcionários Ativos</p>
              <p className="text-2xl font-bold mt-1">{totals.totalActive}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Funcionários</p>
              <p className="text-2xl font-bold mt-1">{totals.totalEmployees}</p>
            </div>
            <Calendar className="w-8 h-8 text-indigo-500" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Folha Base Mensal</p>
              <p className="text-2xl font-bold mt-1">{formatBRL(totals.payrollBase)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Média Salarial</p>
              <p className="text-2xl font-bold mt-1">{formatBRL(totals.averageSalary)}</p>
            </div>
            <Building2 className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold mb-4">Folha por Departamento</h2>
        {byDepartment.length === 0 ? (
          <p className="text-gray-500">Nenhum funcionário ativo encontrado para cálculo.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Departamento
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Funcionários Ativos
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Folha Base
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Comissão Média
                  </th>
                </tr>
              </thead>
              <tbody>
                {byDepartment.map((item) => (
                  <tr key={item.name} className="border-t">
                    <td className="px-4 py-3 font-medium">{item.name}</td>
                    <td className="px-4 py-3">{item.count}</td>
                    <td className="px-4 py-3">{formatBRL(item.payroll)}</td>
                    <td className="px-4 py-3">{item.avgCommissionRate.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

